import '../assets/css/staff-top-tooltip.css';

const controls = new WeakMap();
let activeControl = null;
let nextTooltipId = 0;

const helpText = (value) => typeof value === 'string' ? value.trim() : '';

const cancelFocusShow = (state) => {
  if (state.focusFrame != null) window.cancelAnimationFrame(state.focusFrame);
  state.focusFrame = null;
};

const positionTooltip = (state) => {
  if (!state.tooltip) return;
  const margin = 12;
  const gap = 10;
  const target = state.el.getBoundingClientRect();
  const tooltip = state.tooltip;
  const box = tooltip.getBoundingClientRect();
  const width = document.documentElement.clientWidth;
  const height = window.innerHeight;
  const fitsAbove = target.top >= box.height + gap + margin;
  const top = fitsAbove ? target.top - box.height - gap : target.bottom + gap;
  tooltip.style.left = `${Math.max(margin, Math.min(target.left + (target.width - box.width) / 2, width - box.width - margin))}px`;
  tooltip.style.top = `${Math.max(margin, Math.min(top, height - box.height - margin))}px`;
  tooltip.dataset.placement = fitsAbove ? 'above' : 'below';
};

const hideTooltip = (state) => {
  cancelFocusShow(state);
  clearTimeout(state.hoverTimer);
  state.hoverTimer = null;
  clearTimeout(state.leaveTimer);
  state.detachGlobalListeners?.();
  state.detachGlobalListeners = null;
  if (activeControl === state) activeControl = null;
  if (!state.tooltip) return;
  const description = state.el.getAttribute('aria-describedby');
  const remaining = (description || '').split(/\s+/).filter((id) => id && id !== state.tooltip.id);
  if (remaining.length) {
    state.el.setAttribute('aria-describedby', remaining.join(' '));
  } else if (state.originalDescription === '') {
    state.el.setAttribute('aria-describedby', '');
  } else {
    state.el.removeAttribute('aria-describedby');
  }
  state.tooltip.remove();
  state.tooltip = null;
  state.overTooltip = false;
};

const attachDismissListeners = (state) => {
  if (state.detachGlobalListeners) return;
  const dismiss = () => hideTooltip(state);
  const onKeydown = (event) => { if (event.key === 'Escape') dismiss(); };
  const onPointerdown = (event) => {
    if (!state.el.contains(event.target) && !state.tooltip?.contains(event.target)) dismiss();
  };
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('pointerdown', onPointerdown, true);
  document.addEventListener('scroll', dismiss, true);
  window.addEventListener('resize', dismiss);
  state.detachGlobalListeners = () => {
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onPointerdown, true);
    document.removeEventListener('scroll', dismiss, true);
    window.removeEventListener('resize', dismiss);
  };
};

const scheduleHide = (state) => {
  clearTimeout(state.leaveTimer);
  state.leaveTimer = setTimeout(() => {
    if (!state.overControl && !state.overTooltip && !state.focused) hideTooltip(state);
  }, 180);
};

const showTooltip = (state) => {
  clearTimeout(state.hoverTimer);
  state.hoverTimer = null;
  clearTimeout(state.leaveTimer);
  if (!state.text || !state.el.isConnected) return;
  if (activeControl && activeControl !== state) hideTooltip(activeControl);
  if (state.tooltip) return;

  const tooltip = document.createElement('div');
  tooltip.id = `staff-top-help-${++nextTooltipId}`;
  tooltip.className = 'staff-top-help-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.textContent = state.text;
  tooltip.style.fontFamily = window.getComputedStyle(state.el).fontFamily;
  tooltip.addEventListener('pointerenter', () => {
    state.overTooltip = true;
    clearTimeout(state.leaveTimer);
  });
  tooltip.addEventListener('pointerleave', () => {
    state.overTooltip = false;
    scheduleHide(state);
  });
  document.body.appendChild(tooltip);
  state.tooltip = tooltip;
  activeControl = state;
  state.originalDescription = state.el.getAttribute('aria-describedby');
  state.el.setAttribute('aria-describedby', [state.originalDescription, tooltip.id].filter(Boolean).join(' '));
  positionTooltip(state);
  attachDismissListeners(state);
};

export default {
  mounted(el, binding) {
    const state = { el, text: helpText(binding.value), tooltip: null, focused: false, overControl: false, overTooltip: false };
    const listeners = {
      pointerenter(event) {
        if (event.pointerType === 'touch') return;
        state.overControl = true;
        clearTimeout(state.leaveTimer);
        if (state.tooltip || !state.text) return;
        if (activeControl) hideTooltip(activeControl);
        activeControl = state;
        attachDismissListeners(state);
        state.hoverTimer = setTimeout(() => {
          state.hoverTimer = null;
          if (state.overControl) showTooltip(state);
        }, 2000);
      },
      pointerleave(event) {
        if (event.pointerType === 'touch') return;
        state.overControl = false;
        state.pointerFocus = false;
        if (state.tooltip) scheduleHide(state);
        else hideTooltip(state);
      },
      pointerdown(event) {
        // Touch reveals the explanation without delaying or intercepting the control's action.
        if (event.pointerType === 'touch') showTooltip(state);
        else state.pointerFocus = true;
      },
      pointerup() {
        state.pointerFocus = false;
      },
      pointercancel() {
        state.pointerFocus = false;
      },
      focusin() {
        state.focused = !state.pointerFocus;
        state.pointerFocus = false;
        cancelFocusShow(state);
        if (!state.focused) return;
        // Let the browser finish scrolling the focused control into view first.
        state.focusFrame = window.requestAnimationFrame(() => {
          state.focusFrame = window.requestAnimationFrame(() => {
            state.focusFrame = null;
            if (state.focused) showTooltip(state);
          });
        });
      },
      focusout(event) {
        if (el.contains(event.relatedTarget)) return;
        state.focused = false;
        cancelFocusShow(state);
        scheduleHide(state);
      },
      keydown(event) {
        state.pointerFocus = false;
        if (event.key === 'Escape') hideTooltip(state);
      }
    };
    Object.entries(listeners).forEach(([name, listener]) => el.addEventListener(name, listener));
    state.detachListeners = () => Object.entries(listeners).forEach(([name, listener]) => el.removeEventListener(name, listener));
    controls.set(el, state);
  },
  updated(el, binding) {
    const state = controls.get(el);
    if (!state) return;
    state.text = helpText(binding.value);
    if (!state.text || !el.isConnected) {
      hideTooltip(state);
    } else if (state.tooltip) {
      state.tooltip.textContent = state.text;
      positionTooltip(state);
    }
  },
  beforeUnmount(el) {
    const state = controls.get(el);
    if (!state) return;
    hideTooltip(state);
    state.detachListeners();
    controls.delete(el);
  }
};
